import React, { useCallback, useEffect, useState } from "react";
import { axiosClient } from "../api/proxy/AxiosClient";

import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Grid2,
  Collapse,
  Paper,
  TableContainer,
} from "@mui/material";
import { useServices } from "../services/ServiceContext";
import { usePaymentService } from "../services/PaymentService";
import ActionButtonsPane from "./ActionButtonsPane";

const TeacherSummary = ({ user }) => {

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { lesson } = useServices();
  const [activeCurrency, setActiveCurrency] = useState(null);
  const currentBalance = {};
  const [activeStudent, setActiveStudent] = useState(null);

  const [studentBalances, setStudentBalances] = useState({});

  const { openMakeDeposit } = usePaymentService();
  const actions = [
    {
      label: "Deposit",
      color: "#FF7F00",// orange
      active: () => !user.isDemo && activeStudent && activeCurrency,
      handler: () => {
        console.log("Deposit clicked:", activeStudent);
        openMakeDeposit(user, activeStudent, studentBalances[activeStudent.uid], activeCurrency);
      },
      hintNotActive: `Select Student and currency to make a deposit.`,
      hintActive: `Click to make a Deposit on behalf of ${activeStudent?.name} in ${activeCurrency?.toUpperCase()}.`
    },
  ];

  const studentHasLesson = (studentUid, lesson) => {
    if (!lesson) return false;
    return lesson.students.some(s => s.uid === studentUid);
  }

  const handleCurrencyClick = (ccy) => {
    setActiveCurrency(prev => (prev === ccy ? null : ccy));
  };

  const handleBalanceClick = (ccy, student) => {
    if (ccy !== activeCurrency) {
      setActiveCurrency(ccy);
    }
    if (student.uid !== activeStudent?.uid) {
      setActiveStudent(student);
    }
  };
  
  const handleStudentClick = (student) => {
    setActiveStudent(prev => (prev?.uid === student.uid ? null : student));
  }
 
  const isCurrencySelected = (ccy) => activeCurrency === ccy;

  const getLessonsGroupLength = (group) => {
    const count = group.lessons
      .filter(l => !activeStudent || studentHasLesson(activeStudent.uid, l))
      .length;
    return count;
  }

  const [summary, setSummary] = useState({
    lessons: [],
    lessonGroupsPlanning: {
      ongoing: [],
      upcoming: [],
      requiresAction: [],
      waitingTheOtherSide: [],
      completed: [],
      cancelled: [],
      deleted: [],  
    },
    lessonGroupsBilling : {
      unclear: [],
      outdated: [],
      billed: [],
      paid: [],
    },
    balanceRecords: {},
    balanceTable: {},
    totalsByCurrency: {},
    totalsByStudent: {},
  });

  const viewEditLesson = async (l) => {
    await lesson.openEditLessonForTeacher(l, summary.lessons, (a) => {
      l.status = a.status;
      //setActiveLesson(prev => (prev?.uid === l.uid ? null : l))
    });
  };

  const lessonGroupsPlanning = [
    { key: "ongoing", label: "Ongoing", color: "primary", lessons: summary.lessonGroupsPlanning.ongoing },
    { key: "upcoming", label: "Upcoming", color: "info", lessons: summary.lessonGroupsPlanning.upcoming },
    { key: "requiresAction", label: "Requires action", color: "warning", lessons: summary.lessonGroupsPlanning.requiresAction },
    { key: "waitingTheOtherSide", label: "Waiting the other side", color: "info", lessons: summary.lessonGroupsPlanning.waitingTheOtherSide },
    { key: "completed", label: "Completed", color: "success", lessons: summary.lessonGroupsPlanning.completed },
    { key: "cancelled", label: "Cancelled", color: "default", lessons: summary.lessonGroupsPlanning.cancelled },
  ];
  const [activeGroupKey, setActiveGroupKey] = useState(null);
  const handleGroupClick = (key) => {
    setActiveGroupKey(prev => (prev === key ? null : key));
  };
  const activeGroupPlanning = lessonGroupsPlanning.find(g => g.key === activeGroupKey);

  const lessonGroupsBilling = [
    { key: "unclear", label: "Unclear", color: "primary", lessons: summary.lessonGroupsBilling.unclear },
    { key: "outdated", label: "Outdated", color: "info", lessons: summary.lessonGroupsBilling.outdated },
    { key: "billed", label: "Should be paid", color: "warning", lessons: summary.lessonGroupsBilling.billed },
    { key: "paid", label: "Paid", color: "info", lessons: summary.lessonGroupsBilling.paid },
  ];
  const activeGroupBilling = lessonGroupsBilling.find(g => g.key === activeGroupKey);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [lessonsResponse, paymentsResponse] = await Promise.all([
        axiosClient.get(`/lessons?teacherUid=${user.uid}`),
        axiosClient.get(`/payments/by-teacher/${user.uid}`),
      ]);

      const lessons = (lessonsResponse?.data || []).sort(
        (a, b) => new Date(b.startDttm) - new Date(a.startDttm)
      );

      const payments = paymentsResponse?.data || [];

      const balances = {};
      payments.forEach(tx => {
        if (!balances[tx.debtor.uid]) {
          balances[tx.debtor.uid] = {};
        }
        if (!balances[tx.debtor.uid][tx.ccy]) {
          balances[tx.debtor.uid][tx.ccy] = 0;
        }
    
        let vector = 0;
        if (tx.type === 'deposit') vector = 1;
        else if (tx.type === 'payment') vector = -1;
        balances[tx.debtor.uid][tx.ccy] += (vector * tx.amount);
      });
      setStudentBalances(balances);

      const lessonGroupsPlanning = {
        ongoing: lessons.filter(l => l.status === "ongoing"),
        upcoming: lessons.filter(l =>
          ["submitted", "confirmed"].includes(l.status)
        ),
        requiresAction: lessons.filter(l =>
          ["rescheduled", "requested"].includes(l.status)
        ),
        waitingTheOtherSide: lessons.filter(l =>
          ["proposed", "moved"].includes(l.status)
        ), 
        completed: lessons.filter(l =>
          ["finished", "billed"].includes(l.status)
        ),
        cancelled: lessons.filter(l =>
          ["rejected", "cancelled"].includes(l.status)
        ),
        deleted: lessons.filter(l => 
          l.status === "deleted"
        ),
      };

      const lessonGroupsBilling = {
        unclear: lessons.filter(l => ["proposed", "moved", "rescheduled", "requested"].includes(l.status)),
        outdated: lessons.filter(l => ["proposed", "moved", "rescheduled", "requested"].includes(l.status))
                         .filter(l => new Date(l.endDttm) < new Date()),
        billed: lessons.filter(l =>["finished", "billed", "ongoing"].includes(l.status))
                       .filter(l => l.transactions.reduce((sum, tx) => sum + (tx.type === "payment" ? tx.amount : 0), 0) < (l.fee * l.students.length)),
        paid: lessons.filter(l => l.transactions.length),
      };

      let balanceTable = {
        students: {},
        currencies: new Set(),
      };

      lessons.reduce((acc, lesson) => {
        const { students: debtors, ccy } = lesson;

        debtors.forEach(debtor => {
          if (!acc.students[debtor.uid]) {
            acc.students[debtor.uid] = {
              uid: debtor.uid,
              name: `${debtor.firstName} ${debtor.lastName}`,
              balances: {},
            };
          }

          if (["billed", "finished", "ongoing"].includes(lesson.status)) {
            acc.students[debtor.uid].balances[ccy] =
            (acc.students[debtor.uid].balances[ccy] || 0) - lesson.fee;
      
            acc.currencies.add(ccy);
          }
        });

        return acc;
      }, balanceTable);

      payments.reduce(
        (acc, tx) => {
          const { uid, firstName, lastName } = tx.debtor;
          const ccy = tx.ccy;
      
          if (!acc.students[uid]) {
            acc.students[uid] = {
              uid,
              name: `${firstName} ${lastName}`,
              balances: {},
            };
          }
      
          acc.students[uid].balances[ccy] =
            (acc.students[uid].balances[ccy] || 0) +
            (tx.type === "deposit" ? tx.amount : 0);
      
          acc.currencies.add(ccy);
      
          return acc;
        }, balanceTable || { students: {}, currencies: new Set() });

      // const lessonFinance = {};
      const balanceRecords = {};

      lessons.forEach(l => {
        l.students.forEach(s => {
          balanceRecords[l.uid] = {
            id: l.uid,
            lesson: l,
            student: s,
            fee: l.fee || 0,
            shouldBePaid: ["finished", "billed"].includes(l.status),
            futurePayment: ["confirmed", "submitted", "ongoing"].includes(l.status),
            date: new Date(l.startDttm),
            payments: [],
            currency: l.ccy,
          };
        });
      });

      payments
        // .filter(tx => tx.lesson?.uid)
        // .filter(tx => !activeStudent || !!tx.lesson || tx.debtor.uid === activeStudent.uid)
        .forEach(tx => {
          if (tx.type === "payment") {
            const br = balanceRecords[tx.lesson?.uid];
            if (!br) return;
            br.payments.push(tx);
            // br.paid += tx.amount;
          } else {
            balanceRecords[tx.id] = {
              id: tx.id,
              payments: [tx],
              currency: tx.ccy,
              date: new Date(tx.created_at),
              deposited: tx.amount,
              fee: 0,
              paid: 0
            };
          }
        });

      const totalsByCurrency = Object.values(balanceRecords)
        .filter(br => br.shouldBePaid || br.deposited || br.futurePayment)
        .reduce((acc, br) => {
          acc[br.currency] = acc[br.currency] || { totalFee: 0, totalPaid: 0, totalDeposited: 0 };
          acc[br.currency].totalFee += br.fee || 0;
          acc[br.currency].totalPaid += br.paid || 0;
          acc[br.currency].totalDeposited += br.deposited || 0;
          return acc;
        }, {});

      const totalsByStudent = Object.values(balanceRecords)
        .filter(br => br.shouldBePaid || br.deposited || br.futurePayment)
        .reduce((acc, br) => {
          if (br.lesson) {
            for (const student of br.lesson?.students || []) {
              const studentUid = student.uid || br.payments[0]?.debtor.uid;
              acc[studentUid] = acc[studentUid] || {};
              acc[studentUid][br.currency] = acc[studentUid][br.currency] || { totalFee: 0, totalPaid: 0, totalDeposited: 0 };
              acc[studentUid][br.currency].totalFee += br.fee || 0;
              acc[studentUid][br.currency].totalPaid += br.paid || 0;
              acc[studentUid][br.currency].totalDeposited += br.deposited || 0;
            }
          } else {
            const studentUid = br.payments[0]?.debtor.uid;
            acc[studentUid] = acc[studentUid] || {};
            acc[studentUid][br.currency] = acc[studentUid][br.currency] || { totalFee: 0, totalPaid: 0, totalDeposited: 0 };
            acc[studentUid][br.currency].totalFee += br.fee || 0;
            acc[studentUid][br.currency].totalPaid += br.paid || 0;
            acc[studentUid][br.currency].totalDeposited += br.deposited || 0;
          }
          return acc;
      }, {});

      Object.values(balanceTable.students).reduce((acc, student) => {
        acc[student.uid] = acc[student.uid] || {};
        return acc;
      }, totalsByStudent);

      setSummary({
        lessons,
        lessonGroupsPlanning: lessonGroupsPlanning,
        lessonGroupsBilling: lessonGroupsBilling,
        balanceRecords,
        balanceTable: balanceTable,
        totalsByCurrency,
        totalsByStudent,
      });
    } catch (e) {
      console.error(e);
      setError("Coudn't load student summary. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [user.uid]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ===================== RENDER ===================== */

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">

      {/* -------- LESSONS OVERVIEW -------- */}
      <Card sx={{ mb: 4 }}>
  <CardContent>
    <Typography variant="h6" gutterBottom>
      {`Lessons planning ` + (activeStudent ? ` - ${activeStudent.name}` : '')}
    </Typography>

    {/* KPI-блоки */}
    <Grid2 container spacing={2} mb={2}>
      {lessonGroupsPlanning.map(group => (
        <Grid2 key={group.key}>
          <Card
            variant="outlined"
            onClick={() => handleGroupClick(group.key)}
            sx={{
              cursor: "pointer",
              borderColor: activeGroupKey === group.key ? "primary.main" : undefined,
              bgcolor: activeGroupKey === group.key ? "action.hover" : undefined,
            }}
          >
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {group.label}
              </Typography>
              <Chip
               label={getLessonsGroupLength(group)}
               color={getLessonsGroupLength(group) > 0 ? group.color : "gray"}
              />
            </CardContent>
          </Card>
        </Grid2>
      ))}
    </Grid2>

    {/* Детальный блок */}
    <Collapse in={Boolean(activeGroupPlanning)} timeout="auto" unmountOnExit>
      <Card variant="outlined">
        <CardContent sx={{ p: 0 }}>
          <Typography variant="subtitle1" sx={{ p: 2 }}>
            {activeGroupPlanning?.label} lessons
          </Typography>

          {activeGroupPlanning?.lessons.filter(l => !activeStudent || studentHasLesson(activeStudent.uid, l)).length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 2, pb: 2, fontStyle: "italic" }}
            >
              No lessons in this group
            </Typography>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Lesson</TableCell>
                    <TableCell>Teacher</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {activeGroupPlanning?.lessons
                  .filter(l => !activeStudent || studentHasLesson(activeStudent.uid, l))
                  .map(lesson => (
                    <TableRow
                      key={lesson.uid}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => viewEditLesson(lesson)}
                    >
                      <TableCell>{lesson.name}</TableCell>

                      <TableCell>
                        {`${lesson.teacher?.firstName} ${lesson.teacher?.lastName}`}
                      </TableCell>

                      <TableCell>
                        {new Date(lesson.startDttm).toLocaleDateString()}
                      </TableCell>

                      <TableCell>
                        {new Date(lesson.startDttm).toLocaleTimeString()} -
                        {" "}
                        {new Date(lesson.endDttm).toLocaleTimeString()}
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={lesson.status}
                          size="small"
                          color={
                            lesson.status === "COMPLETED"
                              ? "success"
                              : lesson.status === "CANCELLED"
                              ? "default"
                              : "info"
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Collapse>

    <Typography variant="h6" gutterBottom>
      {'Lessons billing' + (activeStudent ? ` - ${activeStudent.name}` : '')}
    </Typography>

    {/* KPI-блоки */}
    <Grid2 container spacing={2} mb={2}>
      {lessonGroupsBilling.map(group => (
        <Grid2 key={group.key}>
          <Card
            variant="outlined"
            onClick={() => handleGroupClick(group.key)}
            sx={{
              cursor: "pointer",
              borderColor: activeGroupKey === group.key ? "primary.main" : undefined,
              bgcolor: activeGroupKey === group.key ? "action.hover" : undefined,
            }}
          >
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {group.label}
              </Typography>
              <Chip
               label={getLessonsGroupLength(group)}
               color={getLessonsGroupLength(group) > 0 ? group.color : "gray"}
              />
            </CardContent>
          </Card>
        </Grid2>
      ))}
    </Grid2>

    {/* Детальный блок */}
    <Collapse in={Boolean(activeGroupBilling)} timeout="auto" unmountOnExit>
      <Card variant="outlined">
        <CardContent sx={{ p: 0 }}>
          <Typography variant="subtitle1" sx={{ p: 2 }}>
            {activeGroupBilling?.label} lessons
          </Typography>

          {activeGroupBilling?.lessons.filter(l => !activeStudent || studentHasLesson(activeStudent.uid, l)).length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 2, pb: 2, fontStyle: "italic" }}
            >
              No lessons in this group
            </Typography>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Lesson</TableCell>
                    <TableCell>Teacher</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {activeGroupBilling?.lessons
                  .filter(l => !activeStudent || studentHasLesson(activeStudent.uid, l))
                  .map(lesson => (
                    <TableRow
                      key={lesson.uid}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => viewEditLesson(lesson)}
                    >
                      <TableCell>{lesson.name}</TableCell>

                      <TableCell>
                        {`${lesson.teacher?.firstName} ${lesson.teacher?.lastName}`}
                      </TableCell>

                      <TableCell>
                        {new Date(lesson.startDttm).toLocaleDateString()}
                      </TableCell>

                      <TableCell>
                        {new Date(lesson.startDttm).toLocaleTimeString()} -
                        {" "}
                        {new Date(lesson.endDttm).toLocaleTimeString()}
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={lesson.status}
                          size="small"
                          color={
                            lesson.status === "COMPLETED"
                              ? "success"
                              : lesson.status === "CANCELLED"
                              ? "default"
                              : "info"
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Collapse>

  </CardContent>
</Card>



      {/* -------- BALANCE -------- */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid2>
            <Typography variant="h6" gutterBottom>
              Balance by Student
            </Typography>
            <ActionButtonsPane
              actions={actions}
            />
          </Grid2>

          {Object.values(summary.balanceTable.students).length === 0 ? (
            <Typography color="text.secondary">N/A</Typography>
          ) : (
            <TableContainer
              sx={{
                overflowX: 'hidden',
                overflowY: 'hidden',
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Student</TableCell>
                    {Array.from(summary.balanceTable.currencies).sort().map(ccy => (
                      <TableCell
                        key={ccy}
                        align="right"
                        onClick={() => handleCurrencyClick(ccy)}
                        sx={{
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'background-color 0.7s ease, transform 0.3s ease',
                          backgroundColor: activeCurrency === ccy ? "#d0f0d0" : "white",
                          '&:hover': {
                            backgroundColor: 'rgba(103, 211, 88, 0.66)',
                            transform: 'scale(1.2)',
                          },
                        }}
                      >
                        {ccy.toUpperCase()}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {Object.values(summary.balanceTable.students).map(student => (
                    <TableRow
                      key={student.uid}
                    >
                      <TableCell
                        onClick={() => handleStudentClick(student)}
                        sx={{
                          transition: 'background-color 0.7s ease, transform 0.3s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(103, 211, 88, 0.66)',
                            transform: 'translateX(15px)',
                          },
                          backgroundColor: activeStudent === student ? "#d0f0d0" : "white",
                        }}
                      >
                        {student.name}
                      </TableCell>

                      {Array.from(summary.balanceTable.currencies).sort().map(ccy => {
                        const amount = student.balances[ccy] || 0;

                        return (
                          <TableCell
                            key={ccy + student.uid}
                            align="right"
                            sx={{
                              color:
                                amount > 0
                                  ? "success.main"
                                  : amount < 0
                                  ? "error.main"
                                  : "text.secondary",
                              fontWeight: amount !== 0 ? 500 : 400,
                              transition: 'background-color 0.7s ease, transform 0.3s ease',
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: 'rgba(103, 211, 88, 0.66)',
                                transform: 'scale(1.1)',
                              },
                              backgroundColor: activeCurrency === ccy || activeStudent === student ? "#d0f0d0" : "white",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBalanceClick(ccy, student);
                            }}
                          >
                            {amount.toFixed(2)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>


      {/* -------- TOTALS -------- */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {'Totals' + (activeStudent ? ` - ${activeStudent.name}` : '')}
          </Typography>

          {Object.keys(summary.totalsByCurrency).length === 0 || Object.keys(summary.totalsByStudent).length === 0 ? (
            <Typography color="text.secondary">N/A</Typography>
          ) : (
            <Grid2 container spacing={3}>
              {Object.entries(
                activeStudent
                  ? summary.totalsByStudent[activeStudent.uid] || {}
                  : summary.totalsByCurrency
              ).map(
                ([ccy, totals]) => {
                  const outstanding = totals.totalDeposited - totals.totalFee;

                  return (  
                    <Grid2 key={ccy}>
                      <Card
                        variant="outlined"
                        onClick={() => handleCurrencyClick(ccy)}
                         sx={{
                          cursor: "pointer",
                          backgroundColor: isCurrencySelected(ccy) ? "#d0f0d0" : "white",
                          "&:hover": {
                            backgroundColor: isCurrencySelected(ccy) ? "#d0f0d0" : "#e6f2ff",
                          },
                          position: "relative",
                        }}>
                        <CardContent>
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          sx={{ cursor: "pointer" }}
                        >
                          {ccy.toUpperCase()}
                        </Typography>

                          <Divider sx={{ mb: 2 }} />

                          <Typography variant="body2">
                            Total fee: {totals.totalFee.toFixed(2)}
                          </Typography>
                          <Typography variant="body2">
                            Paid: {totals.totalPaid.toFixed(2)}
                          </Typography>
                          <Typography variant="body2">
                            Deposited: {totals.totalDeposited.toFixed(2)}
                          </Typography>
                          <Typography
                            variant="body2"
                            color={
                              outstanding >= 0 ? "success.main" : "error.main"
                            }
                          >
                            Outstanding: {outstanding.toFixed(2)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid2>
                  );
                }
              )}
            </Grid2>
          )}
        </CardContent>
        <Collapse in={Boolean(activeCurrency)} timeout="auto" unmountOnExit>
          <Card sx={{ mb: 4 }} variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Transactions - {activeCurrency?.toUpperCase()}{activeStudent ? ` - ${activeStudent.name}` : ''}
              </Typography>

              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Lesson/Transaction date</TableCell>
                      {activeStudent ? <></> : <TableCell>Student(s)</TableCell>}
                      <TableCell>Lesson status</TableCell>
                      <TableCell align="right">Fee</TableCell>
                      <TableCell align="right">Deposited</TableCell>
                      <TableCell align="right">Paid</TableCell>
                      <TableCell align="right">Current Balance</TableCell>
                      <TableCell align="right">Remaining</TableCell>
                      <TableCell>Transactions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {Object.values(summary.balanceRecords)
                      .filter(br => !activeStudent || br.payments[0]?.debtor.uid === activeStudent?.uid || studentHasLesson(activeStudent.uid, br.lesson))
                      .filter(br => br.currency === activeCurrency && (br.shouldBePaid || br.deposited || br.futurePayment))
                      .sort((a, b) => a.date - b.date)
                      .map(br => {
                        br.paid = br.payments.filter(p => !activeStudent || p.debtor.uid === activeStudent.uid).reduce((sum, tx) => sum + (tx.type === "payment" ? tx.amount : 0), 0);
                        br.remaining = (br.shouldBePaid || br.futurePayment) ? (br.fee * (activeStudent ? 1 : br.lesson.students.length) - br.paid) : 0;
                        let ccyBalance = currentBalance[br.currency];
                        if (!ccyBalance) {
                          ccyBalance = { value: (br.deposited || 0) - (br.paid || br.fee || 0) };
                          currentBalance[br.currency] = ccyBalance;
                        } else {
                          ccyBalance.value += ((br.deposited || 0) - (br.paid || br.fee || 0));
                        }
                        br.balanceValue = ccyBalance.value;
                        return br;
                       })
                      .sort((a, b) => b.date - a.date)
                      .map(br => {
                        return (
                          <TableRow key={br.id} hover>
                            <TableCell>
                              {new Date(br.date).toLocaleDateString()}
                            </TableCell>

                            {
                              activeStudent ? <></> :
                              <TableCell>
                                {br.lesson ? `${br.lesson.students.map(s => s.firstName + ' ' + s.lastName).join(', ')}` : `${br.payments[0]?.debtor.firstName} ${br.payments[0]?.debtor.lastName}`}
                              </TableCell>
                            }

                            <TableCell>
                              {br.lesson?.status}
                            </TableCell>

                            <TableCell align="right">
                              {(br.shouldBePaid || br.futurePayment) && br.fee?.toFixed(2) * (activeStudent ? 1 : br.lesson?.students.length)}
                            </TableCell>

                            <TableCell
                             align="right"
                             sx={{
                               color: "success.main",
                             }}
                            >
                              {br.deposited?.toFixed(2)}
                            </TableCell>

                            <TableCell align="right">
                              {(br.shouldBePaid || br.futurePayment) && br.paid?.toFixed(2)}
                            </TableCell>

                            <TableCell
                              align="right"
                              sx={{
                                color:
                                  br.balanceValue < 0
                                    ? "error.main"
                                    : "success.main",
                              }}
                            >
                              {br.balanceValue.toFixed(2)}
                            </TableCell>

                            <TableCell
                              align="right"
                              sx={{
                                color:
                                  br.remaining > 0
                                    ? "error.main"
                                    : "success.main",
                              }}
                            >
                              {(br.shouldBePaid || br.futurePayment) && br.remaining.toFixed(2)}
                            </TableCell>

                            <TableCell>
                              {br.payments.length === 0 ? (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  No transactions
                                </Typography>
                              ) : (
                                br.payments.map(tx => (
                                  <Box key={tx.id} mb={0.5}>
                                    <Typography variant="body2">
                                      {tx.type}
                                      {" - "}
                                      {tx.amount.toFixed(2)}
                                    </Typography>
                                  </Box>
                                ))
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Collapse>

      </Card>
    </Container>
  );
};

export default TeacherSummary;
